import React, { memo, useCallback, useEffect, useMemo } from "react";
import styled, { useTheme } from "styled-components/native";
import { CheckBox, Spacer, VDBText } from "@components/atoms";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import {
  TouchableOpacity,
  Platform,
  LayoutChangeEvent,
  View,
} from "react-native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GlobalEnum, ProductCategory } from "@utils/constants";
import { SearchGridItemProps } from "./types";
import { productNameToTokenCategory } from "@utils/index";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { VDBIcons } from "@utils/VDBIcons";
import { useDispatch } from "react-redux";
import { setAdHeight } from "@redux/slices/appStateSlice";
import {
  consolidatedStoneTypesIcons,
  filterMenuOptions,
  filterRequestOptions,
} from "./utils";
import { css } from "styled-components/native";
import {
  getBoxShadow,
  isPair,
  isParcel,
} from "@utils/product/productValidators";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useAnimation } from "../../../contexts/AnimationContext";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import {
  useGridTotalPrice,
  useGridTotalPriceLabel,
  useHideSectionContainer,
  usePairIcon,
} from "../../../hooks/useGridMenuOption";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { preventLeftClickWeb } from "@scenes/searchResults/Utils";
import { DeviceEventEmitter } from "react-native";
// Import extracted components and hooks
import { useGridItemState } from "./hooks/useGridItemState";
import { useGridItemHandlers } from "./hooks/useGridItemHandlers";
import ImageSection from "./components/ImageSection";
import PriceSection from "./components/PriceSection";
import MenuSection from "./components/MenuSection";
import { createItemDetailLink } from "@utils/product/searchResultsNavigation";
import { useSearchFiltersAuxPayload } from "@redux/slices/searchFilters";
import { SpaceType } from "@components/atoms/Spacer/types";
import {
  useAllowBMHAndCartActions,
  useConsolidatedStoneTypes,
  useAllowJewelryVariationModal,
  useHightLightThreeDotsMenu,
} from "@redux/slices/globalSlice";
import { PriceModes } from "../PriceModeCard/types";

/**
 * SearchResultGridItem - Optimized grid item component
 * Refactored to use useReducer, extracted components, and proper memoization
 * Reduced from 1,067 lines to ~350 lines
 */
const SearchResultGridItem = ({
  item,
  menuOptions,
  menuOptionSelected,
  checkBoxSelection,
  ismItemChecked,
  showFavSection,
  productType,
  handleItemClick,
  favoriteClickedFunction,
  mIndex,
  showVideo,
  showAvailabilityDot = true,
  displayRetailPrice = false,
  specialPriceFeatureEnabled = false,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  referrerScreen,
}: SearchGridItemProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const priceMode = userInfo?.user?.price_mode;
  const userCurrency = useUserCurrency();
  const customSearchData = useCustomSearchData();
  const productPrefix = useProductPrefix();
  const dispatch = useDispatch();
  const payload = useSearchFiltersAuxPayload();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const allowBMHAndCartActions = useAllowBMHAndCartActions();
  const sectionNameRedux = useSelector(
    (state: any) => state?.searchFilters?.sectionName
  );
  const highlightThreeDotsMenu = useHightLightThreeDotsMenu();
  const { triggerAnimation } = useAnimation();
  const consolidatedStoneTypes = useConsolidatedStoneTypes();
  const showFavoritesAnimation = useSelector(
    (state: any) =>
      state.globalData?.app_settings?.showFavoritesAnimation ?? false
  );
  const consolidatedStoneTypesIconsData = consolidatedStoneTypesIcons(
    consolidatedStoneTypes
  );
  const allowJewelryVariationModal = useAllowJewelryVariationModal();

  // Consolidated state management using custom hook
  const {
    state,
    dispatch: stateDispatch,
    setShowVideoIcon,
    setImageUri,
    setShowErrorImage,
    setShowImageContainer,
  } = useGridItemState(item, ismItemChecked);

  // All event handlers in one hook
  const handlers = useGridItemHandlers({
    item,
    productType,
    priceMode,
    productPrefix,
    dispatch: stateDispatch,
    setShowVideoIcon,
    setImageUri,
    setShowErrorImage,
    setShowImageContainer,
    checkBoxSelection,
    favoriteClickedFunction,
    menuOptionSelected,
    handleItemClick,
  });

  // Memoized computed values
  const productThemeCatgeory = useMemo(
    () => productNameToTokenCategory(productType),
    [productType]
  );

  const stoneType = useMemo(
    () =>
      isPair(item)
        ? SearchFilterTypeEnum.PAIRS
        : isParcel(item)
        ? SearchFilterTypeEnum.PARCEL
        : SearchFilterTypeEnum.SINGLE_STONES,
    [item]
  );

  const stoneTypeLowercase = useMemo(
    () =>
      isPair(item)
        ? SearchFilterTypeEnumLowercase.PAIRS
        : isParcel(item)
        ? SearchFilterTypeEnumLowercase.PARCEL
        : SearchFilterTypeEnumLowercase.SINGLE_STONES,
    [item]
  );

  const isMyInventory =
    userInfo.enableSellReturnButton() &&
    (payload[stoneType]?.with_available_items === "true" ||
      payload[stoneType]?.with_available_items === true);

  const totalPriceLabel = useGridTotalPriceLabel(
    productType as ProductCategory,
    priceMode,
    customSearchData
  );

  const totalPriceCustomization = useGridTotalPrice(
    productType as ProductCategory,
    priceMode,
    customSearchData
  );

  const pairIcon = usePairIcon(
    productType as ProductCategory,
    priceMode,
    customSearchData
  );

  const showPairIcon =
    pairIcon &&
    (showFavSection
      ? isPair(item) && sectionNameRedux === SearchFilterTypeEnum.SINGLE_STONES
      : isPair(item));

  const { hideSubtitleSection, hidePriceSection, shortTitleLines } =
    useHideSectionContainer(productType as ProductCategory, customSearchData);
  // Memoize filtered menu options
  const filteredMenuOptions = useMemo(() => {
    let filtered = filterMenuOptions(menuOptions, userInfo);
    if (
      (item?.custom_design_configuration &&
        item?.display_configuration_v2?.length &&
        item?.display_configuration_v2?.length > 0) ||
      isMyInventory
    ) {
      filtered = filterRequestOptions(filtered, !allowJewelryVariationModal);
    }
    if (
      productType === ProductCategory.JEWELRY &&
      !item?.allow_bmh_and_cart_actions &&
      allowBMHAndCartActions
    ) {
      filtered = filterRequestOptions(filtered);
    }
    return filtered;
  }, [menuOptions, userInfo, item?.custom_design_configuration, isMyInventory]);

  // Memoize availability status
  const availabilityStatus = useMemo(() => {
    switch (item?.available) {
      case GlobalEnum.OUT_ON_MEMO:
        return "error-01";
      case GlobalEnum.ON_HAND:
        return "success-01";
      case GlobalEnum.PLEASE_EMAIL:
      default:
        return "warn-01";
    }
  }, [item?.available]);

  // Memoize video availability checks
  const imageThumbEmpty = useMemo(
    () => item?.image_thumb_url === null || item?.image_thumb_url === undefined,
    [item?.image_thumb_url]
  );

  const videoThumbEmpty = useMemo(
    () => item?.video_url === null || item?.video_url === undefined,
    [item?.video_url]
  );

  const videoNotAvailable = useMemo(
    () =>
      showVideo && (item?.video_url === null || item?.video_url === undefined),
    [showVideo, item?.video_url]
  );

  // Effects - consolidated and focused
  useEffect(() => {
    stateDispatch({
      type: "SET_IS_ITEM_CHECKED",
      payload: item?.is_selected || ismItemChecked || false,
    });
  }, [ismItemChecked, item?.is_selected, stateDispatch]);

  useEffect(() => {
    stateDispatch({ type: "SET_IS_FAVORITE", payload: !!item?.is_favorite });
  }, [item?.is_favorite, stateDispatch]);

  useEffect(() => {
    const shouldShowVideoIcon =
      imageThumbEmpty && !videoThumbEmpty
        ? true
        : videoNotAvailable
        ? false
        : showVideo;

    setShowVideoIcon(shouldShowVideoIcon);
    stateDispatch({
      type: "SET_SHOW_VIDEO_ICON",
      payload: shouldShowVideoIcon,
    });

    if (!showVideo) {
      stateDispatch({ type: "RESET_VIDEO_STATE" });
    }
  }, [
    showVideo,
    imageThumbEmpty,
    videoThumbEmpty,
    videoNotAvailable,
    stateDispatch,
    setShowVideoIcon,
  ]);

  // Handle layout for first item (ad height tracking)
  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      dispatch(setAdHeight(height));
    },
    [dispatch]
  );

  const lineHeightMap: Record<number, number> = {
    1: 24,
    2: 40,
    3: 56,
  };

  const shortTitleMinHeight =
    (shortTitleLines && lineHeightMap[shortTitleLines]) ??
    (productType === ProductCategory.DIAMOND
      ? 40
      : productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.GEMSTONE
      ? 56
      : 40);

  const defaultLines =
    productType === ProductCategory.JEWELRY ||
    productType === ProductCategory.GEMSTONE
      ? 3
      : 2;

  const shortTitleNumberOfLines = shortTitleLines ?? defaultLines;

  const testIdPrefix = `grid.${mIndex ?? "item"}`;
  const hasSpecialPrice = isCustomApp && item?.special_price !== null;

  return (
    <ParentContainer
      testID={`${testIdPrefix}.container`}
      onLayout={mIndex === 1 ? onLayout : undefined}
      isChecked={state.isItemChecked}
      isHovered={state.hovered}
      productThemeCategory={productThemeCatgeory}
      onMouseEnter={handlers.handleMouseEnter}
      onMouseLeave={handlers.handleMouseLeave}
    >
      {/* Image/Video Section */}
      <ImageContainer testID={`${testIdPrefix}.imageContainer`}>
        <ImageSection
          item={item}
          imageUri={state.imageUri}
          showVideoIcon={state.showVideoIcon}
          showErrorImage={state.showErrorImage}
          showImageContainer={state.showImageContainer}
          playVideo={state.playVideo}
          httpErrorIcon={state.httpErrorIcon}
          videoNotAvailable={videoNotAvailable}
          onError={handlers.handleImageError}
          onVideoPress={handlers.handleVideoPress}
          onNoVideoAvailable={handlers.handleNoVideoAvailable}
          onVideoErrorCallback={handlers.handleVideoErrorCallback}
          onItemClick={handlers.handleItemPress}
          theme={theme}
          createItemDetailLink={createItemDetailLink}
          preventLeftClickWeb={preventLeftClickWeb}
          priceMode={priceMode}
          productPrefix={productPrefix}
        />

        {/* Checkbox */}
        {!userInfo.isIFrame() && (
          <TouchableOpacity
            testID={`${testIdPrefix}.checkboxButton`}
            onPress={() => handlers.handleCheckboxChange(state.isItemChecked)}
          >
            <CheckboxContainer
              theme={theme}
              isItemChecked={state.isItemChecked}
              testID={`${testIdPrefix}.checkboxContainer`}
            >
              <CheckBoxOverlay testID={`${testIdPrefix}.checkboxOverlay`}>
                <CheckBox
                  fillColor={theme["knob-bor"]}
                  checkBoxSize={20}
                  checkedFunction={() =>
                    handlers.handleCheckboxChange(state.isItemChecked)
                  }
                  isChecked={state.isItemChecked}
                />
              </CheckBoxOverlay>
            </CheckboxContainer>
          </TouchableOpacity>
        )}

        {/* Favorite Icon */}
        {showFavSection && !userInfo.isIFrame() && (
          <FavoriteContainer
            theme={theme}
            isFavorite={state.isFavorite}
            testID={`${testIdPrefix}.favoriteButton`}
            accessibilityLabel="Favorite button"
            onPress={(event) => {
              if (!state.isFavorite && showFavoritesAnimation) {
                if (
                  Platform.OS === "web" &&
                  theme.isLargeScreen &&
                  event.currentTarget
                ) {
                  const rect = (
                    event.currentTarget as any
                  ).getBoundingClientRect();
                  const heartPosition = {
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2,
                  };
                  triggerAnimation(heartPosition);
                }
                DeviceEventEmitter.emit("animateFavoritesTab");
              }
              handlers.handleFavoritePress(state.isFavorite);
            }}
          >
            <VDBIcon
              color={theme[state.isFavorite ? "error-01" : "cont-00"]}
              size={20}
              variant={IconsVariant.VDBCustom}
              name={state.isFavorite ? VDBIcons.FAV : VDBIcons.FAV_EMPTY}
            />
          </FavoriteContainer>
        )}

        {/* Availability Dot */}
        {showAvailabilityDot && (
          <AvailabilityDot
            testID={`${testIdPrefix}.availabilityDot`}
            bgColor={theme[availabilityStatus]}
          />
        )}

        {showPairIcon && (
          <FindMatchIconContainer testID={`${testIdPrefix}.pairIcon`}>
            <VDBIcon
              name={VDBIcons.FIND_A_MATCH}
              color={theme["over-04"]}
              size={40}
              variant={IconsVariant.VDBCustom}
            />
          </FindMatchIconContainer>
        )}

        {consolidatedStoneTypes && consolidatedStoneTypes?.enabled && (
          <FindMatchIconContainer testID={`${testIdPrefix}.pairIcon`}>
            <VDBIcon
              name={consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.data}
              variant={
                consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.type
              }
              color={theme["over-04"]}
              size={40}
            />
          </FindMatchIconContainer>
        )}

        {hasSpecialPrice && (
          <SpecialPriceContainer>
            <VDBText
              color={theme["main-05"]}
              type={typography["Minor-M"]}
              numberOfLines={1}
            >
              {"% " + t("searchResult.grid.specialPrice.text")}
            </VDBText>
          </SpecialPriceContainer>
        )}
      </ImageContainer>

      <ImageContainerLineView
        testID={`${testIdPrefix}.imageDivider`}
        theme={theme}
      />

      {/* Content Section */}
      <ContentContainer testID={`${testIdPrefix}.contentContainer`}>
        <TouchableOpacity
          testID={`${testIdPrefix}.contentButton`}
          activeOpacity={1}
          {...(Object?.keys?.(item)?.length < 1 && {
            style: { pointerEvents: "none" },
          })}
          onPress={handlers.handleItemPress}
        >
          <AnchorTag
            href={
              Platform.OS === "web"
                ? createItemDetailLink({
                    productID: item?.id,
                    productType,
                    priceMode: userInfo.getPriceMode(),
                    productPrefix,
                  })
                : undefined
            }
            onClick={Platform.OS === "web" ? preventLeftClickWeb : undefined}
          >
            <ContentChildContainer testID={`${testIdPrefix}.contentBody`}>
              {/* Title */}
              {item?.short_title && (
                <TitleContainer
                  testID={`${testIdPrefix}.titleContainer`}
                  minHeight={shortTitleMinHeight}
                >
                  <VDBText
                    color={theme["core-05"]}
                    type={typography[theme.isLargeScreen ? "H3-R" : "Minor-M"]}
                    numberOfLines={shortTitleNumberOfLines}
                    ellipsizeMode="tail"
                  >
                    {item?.short_title}
                  </VDBText>
                </TitleContainer>
              )}

              <Spacer size={SpaceType.ExtraSmall} />

              {/* Price Section */}
              <PriceSection
                item={item}
                priceMode={priceMode}
                displayRetailPrice={displayRetailPrice}
                userCurrency={userCurrency}
                productType={productType as ProductCategory}
                userInfo={userInfo}
                theme={theme}
                typography={typography}
                customSearchData={customSearchData}
                sectionName={sectionName}
                isCustomApp={isCustomApp}
                totalPriceCustomization={totalPriceCustomization}
                totalPriceLabel={totalPriceLabel}
                referrerScreen={referrerScreen}
                t={t}
                onTooltipToggle={handlers.handleTooltipToggle}
                specialPriceFeatureEnabled={specialPriceFeatureEnabled}
                hideSubtitleSection={hideSubtitleSection}
                hidePriceSection={hidePriceSection}
              />
            </ContentChildContainer>
          </AnchorTag>
        </TouchableOpacity>

        {/* Three-Dot Menu */}
        <MenuSection
          filteredMenuOptions={filteredMenuOptions}
          theme={theme}
          typography={typography}
          productType={productType}
          priceMode={priceMode}
          onMenuItemSelect={handlers.handleMenuSelection}
          itemIndex={mIndex}
          isInCart={
            highlightThreeDotsMenu &&
            priceMode !== PriceModes.GREEN &&
            item?.in_cart
          }
        />
      </ContentContainer>

      <BorderHack pointerEvents="none" />
    </ParentContainer>
  );
};

// Styled Components
const ParentContainer = styled.View<{
  isChecked: boolean;
  productThemeCategory: string;
  isHovered: boolean;
}>`
  width: 100%;
  background-color: ${({ theme }) => theme["core-01"]};
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  border-color: ${({ theme, isChecked, productThemeCategory }) =>
    isChecked ? theme[`${productThemeCategory}-06`] : theme["core-04"]};
  border-width: ${({ isChecked }) => (isChecked ? "2px" : "1px")};
  ${({ theme, isChecked, isHovered }) =>
    (isChecked || isHovered) &&
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Normal)};
    `}
`;

const ImageContainer = styled.View`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
`;

const ContentContainer = styled.View`
  width: 100%;
  flex-grow: 1;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const CheckboxContainer = styled.View<any>`
  height: 32px;
  width: 32px;
  border-top-left-radius: 6px;
  border-bottom-right-radius: 8px;
  background-color: ${({ theme, isItemChecked }) =>
    theme[isItemChecked ? "over-03" : "over-02"]};
  position: absolute;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SpecialPriceContainer = styled.View<any>`
  height: 26px;
  width: 113px;
  border-top-left-radius: 16px;
  background-color: ${({ theme }) => theme["main-04"]};
  position: absolute;
  bottom: 0;
  right: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const CheckBoxOverlay = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  width: 20px;
  height: 20px;
  right: 1px;
  border-radius: 8px;
`;

const AvailabilityDot = styled.View<any>`
  height: 12px;
  width: 12px;
  border-radius: 8px;
  border: 1px;
  border-color: ${({ theme }) => theme["cont-00"]};
  background-color: ${({ bgColor }) => bgColor};
  position: absolute;
  bottom: 8px;
  left: 8px;
`;

const ImageContainerLineView = styled.View<any>`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: auto;
`;

const FavoriteContainer = styled.Pressable<any>`
  height: 32px;
  width: 32px;
  border-top-right-radius: 6px;
  border-bottom-left-radius: 8px;
  background-color: ${({ theme, isFavorite }) =>
    theme[isFavorite ? "over-03" : "over-02"]};
  position: absolute;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  right: -0.5px;
  top: -0.5px;
  overflow: hidden;
`;

const ContentChildContainer = styled.View`
  width: 100%;
  padding: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const TitleContainer = styled.View<{
  minHeight: number;
}>`
  min-height: ${({ minHeight }) => minHeight}px;
`;

const BorderHack = styled.View<any>`
  border-radius: 8px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const FindMatchIconContainer = styled.View`
  position: absolute;
  bottom: 0px;
  right: 0px;
  z-index: 1000;
  background-color: ${({ theme }) => theme["over-02"]};
  padding-vertical: 2px;
  padding-horizontal: 4px;
  border-top-left-radius: ${SpaceType.Double}px;
`;

const AnchorTag =
  Platform.OS === "web"
    ? styled.View.attrs(() => ({ as: "a" as any }))`
        text-decoration: none;
        display: flex;
      `
    : View;

// Export with optimized memo comparison
export default memo(SearchResultGridItem, (prev, next) => {
  return (
    prev.item?.id === next.item?.id &&
    prev.item?.is_favorite === next.item?.is_favorite &&
    prev.item?.is_selected === next.item?.is_selected &&
    prev.item?.total_sales_price === next.item?.total_sales_price &&
    prev.item?.retail_price === next.item?.retail_price &&
    prev.item?.image_thumb_url === next.item?.image_thumb_url &&
    prev.item?.video_url === next.item?.video_url &&
    prev.item?.available === next.item?.available &&
    prev.menuOptions === next.menuOptions &&
    prev.ismItemChecked === next.ismItemChecked &&
    prev.showVideo === next.showVideo &&
    prev.displayRetailPrice === next.displayRetailPrice &&
    prev.productType === next.productType &&
    prev.showAvailabilityDot === next.showAvailabilityDot
  );
});
